import React from 'react';
import styled from 'styled-components';
import { color, gutter } from '../../styles/mixins';
import CheckmarkSvg from '../svg/checkmark';

const Checkmark = styled.span<{ checked?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;

  height: 20px;
  width: 20px;
  min-width: 20px;
  min-height: 20px;
  background-color: ${color('white')};
  border: 1px solid;
  border-color: ${(props) =>
    props.checked ? color('navy100') : color('gray70')};
  border-radius: ${(props) => props.theme.border.radius}px;

  transition-property: background-color, border-color;
  transition-duration: ${(props) => props.theme.transition.duration};
  transition-timing-function: ${(props) => props.theme.transition.timing};

  &:after {
    content: '';
    position: absolute;
    display: none;
  }

  > svg {
    visibility: hidden;
    opacity: 0;

    transition-property: opacity;
    transition-duration: ${(props) => props.theme.transition.duration};
    transition-timing-function: ${(props) => props.theme.transition.timing};
  }
`;

const Label = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &:hover input ~ ${Checkmark} {
    background-color: ${color('gray20')};
  }

  input:focus ~ ${Checkmark} {
    background-color: ${color('gray20')};
  }

  input:checked ~ ${Checkmark} {
    background-color: ${color('navy100')};
    border-color: ${color('navy100')};
  }

  input:checked ~ ${Checkmark} > svg {
    visibility: visible;
    opacity: 1;
  }

  input:checked:hover ~ ${Checkmark} {
    background-color: ${color('blue60')};
    border-color: ${color('blue60')};
  }

  input:checked ~ ${Checkmark}:after {
    display: block;
  }

  input:disabled ~ ${Checkmark}, input:disabled:hover ~ ${Checkmark} {
    background-color: ${color('navy100')};
    border-color: ${color('navy100')};
    opacity: 0.2;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const Text = styled.span`
  margin-left: ${gutter(1.5)};
`;

interface CheckBoxProps extends React.ComponentPropsWithoutRef<'input'> {
  children: React.ReactNode;
}

export const CheckBox = ({ children, ...props }: CheckBoxProps) => {
  return (
    <Label>
      <Input type="checkbox" {...props} />
      <Checkmark>
        <CheckmarkSvg />
      </Checkmark>
      <Text>{children}</Text>
    </Label>
  );
};
